import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataLabel,
  DataValue,
  DataValueWithUnit,
  DataUnit,
  ExpandableList,
  ExpandableItem,
  ExpandableHeader,
  ExpandableTitle,
  ExpandableSubtitle,
  ExpandableContent,
  ExpandableDetails,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { ExpandArrow } from '../../../atoms/ExpandArrow'
import { Icon } from '../../../atoms/Icon'
import { formatNumberSwiss } from '../../../../utils/number/formatUtils'
import { DwellingsSectionProps } from './types'

const ExpandButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  margin-top: ${({ theme }) => theme.spacing.medium};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`

const DwellingNumber = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  color: ${({ theme }) => theme.colors.text.primary};
`

const DwellingSeparator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin: 0 ${({ theme }) => theme.spacing.xsmall};
`

const DwellingFloor = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
`

const CountBadge = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: ${({ theme }) => theme.colors.background.tertiary};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`

const FeaturesContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
  flex-wrap: wrap;
`

const FeatureBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const PricePredictionCard = styled.div`
  margin-top: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.default};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primaryLight} 0%,
    rgba(2, 148, 223, 0.05) 100%
  );
  border: 1px solid rgba(2, 148, 223, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`

const PricePredictionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`

const PriceBlock = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  &:last-child {
    margin-bottom: 0;
  }
`

const PriceLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MainPrice = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const PriceUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: ${({ theme }) => theme.spacing.xsmall};
`

const PriceRangeBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  position: relative;
`

const PriceRangeFill = styled.div<{ $percentage: number; $color: string }>`
  position: absolute;
  left: 25%;
  width: 50%;
  height: 100%;
  background: ${({ theme, $color }) =>
    $color === 'success' ? theme.colors.success : theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.round};

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    background: ${({ theme }) => theme.colors.background.primary};
    border: 2px solid currentColor;
    border-radius: 50%;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.background.primary};
  }
`

const PriceRange = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.large};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

const DetailsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  svg {
    width: 12px;
    height: 12px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};

    svg {
      transform: translateX(2px);
    }
  }
`

export const DwellingsSection: React.FC<DwellingsSectionProps> = ({ building, detailsLink }) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  const [showIndividualDwellings, setShowIndividualDwellings] = useState(false)
  const [expandedDwellings, setExpandedDwellings] = useState<Set<string>>(new Set())

  const toggleDwelling = (id: string) => {
    const newExpanded = new Set(expandedDwellings)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedDwellings(newExpanded)
  }

  const allDwellings: any[] = []
  building.buildings_administrative?.forEach((admin: any) => {
    admin.addresses?.forEach((address: any) => {
      address.dwellings?.forEach((dwelling: any) => {
        allDwellings.push(dwelling)
      })
    })
  })

  const sortedDwellings = [...allDwellings].sort((a, b) => {
    const floorA = a.floor ?? Infinity
    const floorB = b.floor ?? Infinity
    if (floorA !== floorB) return floorA - floorB

    const areaA = a.area ?? 0
    const areaB = b.area ?? 0
    if (areaA !== areaB) return areaB - areaA

    const roomsA = a.room_nb ?? 0
    const roomsB = b.room_nb ?? 0
    return roomsB - roomsA
  })

  const dwellingsCount = sortedDwellings.length || building.dwellings_nb || 0

  if (dwellingsCount === 0) {
    return (
      <InfoSection>
        <SectionHeader>
          <SectionTitle>
            <SectionIcon>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3zm5 15h-2v-6H9v6H7v-7.81l5-4.5 5 4.5V18z" />
              </svg>
            </SectionIcon>
            {t('buildingPanel.dwellings.title')}
          </SectionTitle>
        </SectionHeader>
        <SectionContent>
          <EmptyState>{t('buildingPanel.dwellings.noDwellings')}</EmptyState>
        </SectionContent>
      </InfoSection>
    )
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3zm5 15h-2v-6H9v6H7v-7.81l5-4.5 5 4.5V18z" />
            </svg>
          </SectionIcon>
          {t('buildingPanel.dwellings.title')}
        </SectionTitle>
        <SectionCount>{dwellingsCount}</SectionCount>
      </SectionHeader>
      <SectionContent>
        <DataGrid>
          <DataRow>
            <DataLabel>{t('buildingPanel.dwellings.totalDwellings')}</DataLabel>
            <DataValue $large>{dwellingsCount}</DataValue>
          </DataRow>

          {building.dwellings_total_area && (
            <DataRow>
              <DataLabel>{t('buildingPanel.dwellings.totalArea')}</DataLabel>
              <DataValueWithUnit>
                <DataValue $large>{formatNumberSwiss(building.dwellings_total_area)}</DataValue>
                <DataUnit>m²</DataUnit>
              </DataValueWithUnit>
            </DataRow>
          )}

          {building.dwelling_median_area && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('buildingPanel.dwellings.medianArea')}
                explanation={tCommon('propertyExplanations.dwellings.medianArea')}
              >
                {t('buildingPanel.dwellings.medianArea')}
              </DataLabelWithTooltip>
              <DataValueWithUnit>
                <DataValue>{formatNumberSwiss(building.dwelling_median_area)}</DataValue>
                <DataUnit>m²</DataUnit>
              </DataValueWithUnit>
            </DataRow>
          )}

          {building.dwelling_median_room_nb && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('buildingPanel.dwellings.medianRooms')}
                explanation={tCommon('propertyExplanations.dwellings.medianRooms')}
              >
                {t('buildingPanel.dwellings.medianRooms')}
              </DataLabelWithTooltip>
              <DataValue>{building.dwelling_median_room_nb}</DataValue>
            </DataRow>
          )}

          {building.dwelling_min_area && building.dwelling_max_area && (
            <DataRow>
              <DataLabel>{t('buildingPanel.dwellings.sizeRange')}</DataLabel>
              <DataValue>
                {formatNumberSwiss(building.dwelling_min_area)} m² -{' '}
                {formatNumberSwiss(building.dwelling_max_area)} m²
                {building.dwelling_min_room_nb && building.dwelling_max_room_nb && (
                  <>
                    {' \u2022 '}
                    {building.dwelling_min_room_nb}-{building.dwelling_max_room_nb} rm
                  </>
                )}
              </DataValue>
            </DataRow>
          )}

          {building.dwelling_last_construction_year && (
            <DataRow>
              <DataLabel>{t('buildingPanel.dwellings.lastConstruction')}</DataLabel>
              <DataValue>{building.dwelling_last_construction_year}</DataValue>
            </DataRow>
          )}
        </DataGrid>

        {detailsLink ? (
          <DetailsLink href={detailsLink}>
            <Icon name="arrow-right" size="small" />
            {t('buildingPanel.dwellings.viewIndividual')} (
            {sortedDwellings.length || dwellingsCount})
          </DetailsLink>
        ) : sortedDwellings.length > 0 ? (
          <>
            <ExpandButton onClick={() => setShowIndividualDwellings(!showIndividualDwellings)}>
              <Icon name={showIndividualDwellings ? 'chevron-up' : 'chevron-down'} size="small" />
              {showIndividualDwellings
                ? t('buildingPanel.dwellings.hideIndividual')
                : t('buildingPanel.dwellings.viewIndividual')}{' '}
              ({sortedDwellings.length})
            </ExpandButton>

            {showIndividualDwellings && (
              <div style={{ marginTop: '16px' }}>
                <ExpandableList>
                  {sortedDwellings.map((dwelling: any, index: number) => {
                    const uniqueId = String(dwelling.dwelling_id || dwelling.id || index)
                    const isExpanded = expandedDwellings.has(uniqueId)

                    return (
                      <ExpandableItem key={uniqueId} $expanded={isExpanded}>
                        <ExpandableHeader onClick={() => toggleDwelling(uniqueId)}>
                          <ExpandableTitle>
                            {dwelling.room_nb && (
                              <>
                                <DwellingNumber>
                                  {dwelling.room_nb} {t('buildingPanel.dwellings.rooms')}
                                </DwellingNumber>
                                <DwellingSeparator>{'\u2022'}</DwellingSeparator>
                              </>
                            )}
                            {dwelling.area && (
                              <>
                                <DwellingNumber>
                                  {formatNumberSwiss(dwelling.area)} m²
                                </DwellingNumber>
                                {dwelling.multiple_floor && (
                                  <>
                                    <DwellingSeparator>{'\u2022'}</DwellingSeparator>
                                    <DwellingFloor>
                                      {t('buildingPanel.dwellings.multiFloor')}
                                    </DwellingFloor>
                                  </>
                                )}
                              </>
                            )}
                            {!dwelling.room_nb && !dwelling.area && (
                              <DwellingNumber>
                                {t('buildingPanel.dwellings.dwellingNumber')} #{index + 1}
                              </DwellingNumber>
                            )}
                          </ExpandableTitle>
                          <ExpandableSubtitle>
                            {dwelling.floor !== undefined && (
                              <>
                                <DwellingFloor>
                                  {t('buildingPanel.dwellings.floor')} {dwelling.floor}
                                </DwellingFloor>
                                <DwellingSeparator>{'\u2022'}</DwellingSeparator>
                              </>
                            )}
                            <CountBadge>
                              {index + 1}/{sortedDwellings.length}
                            </CountBadge>
                            <ExpandArrow $expanded={isExpanded} direction="down" />
                          </ExpandableSubtitle>
                        </ExpandableHeader>

                        <ExpandableContent $expanded={isExpanded}>
                          <ExpandableDetails>
                            <DataGrid $gap={0}>
                              {dwelling.area && (
                                <DataRow>
                                  <DataLabel>{t('buildingPanel.dwellings.area')}</DataLabel>
                                  <DataValueWithUnit>
                                    <DataValue>{formatNumberSwiss(dwelling.area)}</DataValue>
                                    <DataUnit>m²</DataUnit>
                                  </DataValueWithUnit>
                                </DataRow>
                              )}

                              {dwelling.room_nb !== undefined && (
                                <DataRow>
                                  <DataLabel>{t('buildingPanel.dwellings.rooms')}</DataLabel>
                                  <DataValue>{dwelling.room_nb}</DataValue>
                                </DataRow>
                              )}

                              {dwelling.floor !== undefined && (
                                <DataRow>
                                  <DataLabel>{t('buildingPanel.dwellings.floor')}</DataLabel>
                                  <DataValue>{dwelling.floor}</DataValue>
                                </DataRow>
                              )}

                              {(dwelling.has_kitchen !== undefined ||
                                dwelling.multiple_floor !== undefined) && (
                                <DataRow>
                                  <DataLabel>{t('buildingPanel.dwellings.features')}</DataLabel>
                                  <FeaturesContainer>
                                    {dwelling.has_kitchen && (
                                      <FeatureBadge>
                                        <Icon name="utensils" size={12} />
                                        {t('buildingPanel.dwellings.kitchen')}
                                      </FeatureBadge>
                                    )}
                                    <FeatureBadge>
                                      <Icon name="layers" size={12} />
                                      {dwelling.multiple_floor
                                        ? t('buildingPanel.dwellings.multiFloor')
                                        : t('buildingPanel.dwellings.singleFloor')}
                                    </FeatureBadge>
                                  </FeaturesContainer>
                                </DataRow>
                              )}

                              {dwelling.status && (
                                <DataRow>
                                  <DataLabel>{t('buildingPanel.dwellings.status')}</DataLabel>
                                  <DataValue>{dwelling.status}</DataValue>
                                </DataRow>
                              )}
                            </DataGrid>

                            {dwelling.price_prediction && (
                              <PricePredictionCard>
                                <PricePredictionHeader>
                                  <Icon name="dollar-sign" size={16} />
                                  {t('buildingPanel.dwellings.pricePredictions')}
                                </PricePredictionHeader>

                                {dwelling.price_prediction.purchase && (
                                  <PriceBlock>
                                    <PriceLabel>
                                      {t('buildingPanel.dwellings.purchasePrice')}
                                    </PriceLabel>
                                    <MainPrice>
                                      CHF{' '}
                                      {formatNumberSwiss(
                                        dwelling.price_prediction.purchase.price || 0
                                      )}
                                    </MainPrice>
                                    {dwelling.price_prediction.purchase.lower_bound &&
                                      dwelling.price_prediction.purchase.upper_bound && (
                                        <>
                                          <PriceRangeBar>
                                            <PriceRangeFill $percentage={50} $color="success" />
                                          </PriceRangeBar>
                                          <PriceRange>
                                            CHF{' '}
                                            {formatNumberSwiss(
                                              dwelling.price_prediction.purchase.lower_bound
                                            )}
                                            {' - '}
                                            {formatNumberSwiss(
                                              dwelling.price_prediction.purchase.upper_bound
                                            )}
                                          </PriceRange>
                                        </>
                                      )}
                                  </PriceBlock>
                                )}

                                {dwelling.price_prediction.rent && (
                                  <PriceBlock>
                                    <PriceLabel>
                                      {t('buildingPanel.dwellings.monthlyRent')}
                                    </PriceLabel>
                                    <MainPrice>
                                      CHF{' '}
                                      {formatNumberSwiss(dwelling.price_prediction.rent.price || 0)}
                                      <PriceUnit>{t('buildingPanel.dwellings.perMonth')}</PriceUnit>
                                    </MainPrice>
                                    {dwelling.price_prediction.rent.lower_bound &&
                                      dwelling.price_prediction.rent.upper_bound && (
                                        <>
                                          <PriceRangeBar>
                                            <PriceRangeFill $percentage={50} $color="primary" />
                                          </PriceRangeBar>
                                          <PriceRange>
                                            CHF{' '}
                                            {formatNumberSwiss(
                                              dwelling.price_prediction.rent.lower_bound
                                            )}
                                            {' - '}
                                            {formatNumberSwiss(
                                              dwelling.price_prediction.rent.upper_bound
                                            )}
                                          </PriceRange>
                                        </>
                                      )}
                                  </PriceBlock>
                                )}
                              </PricePredictionCard>
                            )}
                          </ExpandableDetails>
                        </ExpandableContent>
                      </ExpandableItem>
                    )
                  })}
                </ExpandableList>
              </div>
            )}
          </>
        ) : null}
      </SectionContent>
    </InfoSection>
  )
}
